import React, {
    MutableRefObject,
    ReactElement,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { useFormContext, UseFormMethods, ValidateResult } from '../../../Vendor/react-hook-form/dist/index.ie11'
import {
    CxiLicenceTypes,
    CXRecordLanguage,
    CXScoreLanguage,
    ReportingLanguage,
    RoleLanguage,
    Timezone,
    UserDetailsLanguage
} from '../../../../i18n'
import { EMAIL_REGEX } from '../../../NonReact/constants/Regexes'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty, isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import FormRow from '../../Common/Components/FormRow'
import Heading, { HeadingLevel } from '../../Common/Components/Heading'
import { IconName } from '../../Common/Components/Icon'
import { InputAdornmentButton } from '../../Common/Components/InputAdornmentButton/InputAdornmentButton'
import { InputHelp } from '../../Common/Components/InputHelp/InputHelp'
import { getRandomPassword } from '../../Common/Components/PasswordTextInput/PasswordTextInput'
import { GroupedSelect, StandardOptionGroup } from '../../Common/Components/Selects/GroupedSelect/GroupedSelect'
import { getFieldError, ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { ReactState } from '../../Common/Contexts'
import UserInfoContext, { UserInfoContextType } from '../../Common/Contexts/UserInfoContext'
import { LanguageConfig } from '../../Common/Types/Types'
import { DEFAULT_PASSWORD_VALUE, isTranscribeQualifiedForDevice } from '../AddUserMapper'
import { LANGUAGE_DEFAULT, SERVER_NONE_VALUE } from '../Constants'
import {
    CXI_DATA_ENTITLEMENT_NAME,
    CXI_LICENCE_NAME,
    CXR_SUBSCRIPTION_NAME,
    CXT_TRANSCRIBE_ENABLED_NAME,
    CXS_SUBSCRIPTION_NAME,
    DEFAULT_LANG_COMPONENT_NAME,
    EMAIL_COMPONENT_NAME,
    ENABLE_MFA_COMPONENT_NAME,
    ENDPOINT_AGENT_NAME,
    ENDPOINT_DEVICE_NAME,
    FULLNAME_COMPONENT_NAME,
    PASSWORD_CHANGE_COMPONENT_NAME,
    PASSWORD_COMPONENT_NAME,
    REPORTING_LEVEL_NAME,
    ROLE_COMPONENT_NAME,
    SEND_WELCOME_COMPONENT_NAME,
    TIMEZONE_COMPONENT_NAME,
    USERNAME_COMPONENT_NAME
} from '../FieldNames'
import { ReportingAccessLevels } from '../Reporting/ReportingAccessLevels'
import { RoleTemplate } from '../Roles/Templates'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { CxsTelsysMode } from '../../../NonReact/constants/CxsTelsysModes'
import {
    cxsAgentScopeReplacement,
    cxsReplacementForUserType,
    cxsSelectOptionsForAddUser,
    ensureCurrentCxsOptionVisible,
    shouldPreserveLoadedCxsSubscription,
    withoutCxsAgentOption
} from '../../../NonReact/utils/CxsSubscriptionAccessUtils'
import { HandleChange, SelectOptions, SelectOptionValues } from '../../Common/Components/Selects/Types'
import { BusinessOption, TelSysTypes } from '../../TelSysConfiguration/Constants/Constants'
import { TimezoneKeys, timezonesToSelectOptions } from '../../TimezoneModal/Mappers/Timezone'
import { isCxrProductApplicable } from '../../Common/Utils/CxProductApplicability'
import { DEFAULT_TIMEZONE } from '../../TimezoneModal/Constants/Constants'
import { AutocompleteValues } from '../../../NonReact/constants/FormConstants'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { OutlinedSelect } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedSelect'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'
import { OutlinedPasswordTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedPasswordTextInput'
import { CxIntegrationLicence } from '../../../NonReact/common/Types/SelfProvisioning/SelfProvisioningTypes'
import { RulesUtils } from '../../Common/Rules/Rules'
import { CXScoreLicences } from '../../../NonReact/constants/CXScoreLicences'
import { CXRecordLicences } from '../../../NonReact/constants/CXRecordLicences'
import {
    CxRecordLicenceSelectOption,
    cxrRestrictedReplacement,
    cxrSelectOptionsForAddUser,
    ensureCurrentCxrOptionVisible,
    isCxrManagerAllowedLevel,
    shouldPreserveLoadedCxrSubscription
} from '../../../NonReact/utils/CxrSubscriptionAccessUtils'
import { DeviceMonitoring } from '../Types'

type Props = {
    defaultUsername?: string
    selectedUser?: string
    isAdmin?: boolean
    viewOnly?: boolean
    hasPermissionsChanged: boolean
    languageConfig: Nullable<LanguageConfig>
    loadedCxsSubscription?: CXScoreLicences
    loadedCxrSubscription?: CXRecordLicences
    /** The CX Score and CX Record flags on the device the user loaded with, when editing a user. */
    loadedDeviceMonitoring?: DeviceMonitoring
    /** The user's saved CXI licence, when editing a user. */
    loadedCxiLicence?: CxIntegrationLicence
    loadedReportingLevel?: string
    allRoles: RoleTemplate[]
    reportingLevelOptions: Nullable<StandardOptionGroup>[]
    handleReportingLevelChange: HandleChange
    isDesktopOnlyMode: Definable<boolean>
    telSysType: Nullable<TelSysTypes>
    /**
     * Whether CX Analytics applies to the telsys. Gates the reporting-level (CX Analytics) dropdown:
     * on MS Teams it requires the CX-Analytics business option (Legacy and Business Historic aside),
     * while every non-Teams telsys keeps offering it. When false the dropdown is hidden; a new user's
     * reporting level is reset to None, while an existing user's loaded level is preserved.
     */
    isCxAnalyticsApplicable: boolean
    reportLevelOptionsLoading: boolean
    /**
     * When true, CX Score is enabled, TelSys is BroadWorks or MS Teams, and the permissions scope qualifies: one
     * concrete partition for BroadWorks, one concrete partition or all partitions for MS Teams.
     */
    isCxsSubscriptionApplicable: boolean
    /**
     * When false, the permissions scope covers all partitions, so CXS Agent is not offered and an unsaved CXS Agent
     * selection is reset to None (the backend requires a single concrete partition for CXS Agent).
     */
    isCxsAgentScopeAllowed: boolean
    businessIntegrationOptions?: readonly BusinessOption[]
    /**
     * The telephony server's CXS tier-mapping mode ('teamsBusiness' or 'broadworks'), or undefined for the legacy
     * behaviour. Controls how CXS options are restricted per reporting tier (see {@link CxsTelsysMode}).
     */
    cxsTelsysMode?: CxsTelsysMode
    showCxiUserField: boolean
    showCxiDataField: boolean
    /**
     * Whether the user's CME allows CX Score for the TelSys type (APD-13052). When false, a user who already
     * holds a CXS licence can only keep it or set it to None, and everyone else gets no CXS field.
     */
    isCxsCmeCapabilityEnabled?: boolean
    /**
     * Whether the user's CME allows CX Record for the TelSys type (APD-13052). When false, a user who already
     * holds a CXR licence can only keep it or set it to None, and everyone else gets no CXR field.
     */
    isCxrCmeCapabilityEnabled?: boolean
    /**
     * Whether the user's CME allows CX Integration for the TelSys type (APD-13052). When false, a user who already
     * holds a CXI licence can only keep it or set it to None.
     */
    isCxiCmeCapabilityEnabled?: boolean
    /**
     * Whether Transcribe can be offered at all: the feature flag is on and the user's CME allows CX Transcribe for
     * the TelSys type (APD-14047). Without a qualifying licence the toggle shows greyed out, not hidden.
     */
    isTranscribeAvailable?: boolean
    /** True when the user's TelSys is BroadWorks (or a Simulator emulating it), where only CX Score qualifies. */
    isBroadWorksForTranscribe?: boolean
}

/** The licence values the Transcribe auto-off last saw, so it can tell what changed. */
type TranscribeLicences = {
    readonly cxs: Definable<string>
    readonly cxr: Definable<string>
    readonly isQualified: boolean
}

/**
 * Limits licence options to the user's current licence and None, for a product the CME no longer allows.
 *
 * @param options The options that would otherwise be offered.
 * @param noneValue The licence's None value.
 * @param loadedValue The user's saved licence, when they hold one.
 * @returns The None option plus the saved licence's option.
 */
const keepOnlyLoadedOrNone = <T extends { value: string }>(
    options: readonly T[],
    noneValue: string,
    loadedValue: Definable<string>
): T[] => options.filter((option: T): boolean => option.value === noneValue || option.value === loadedValue)

/**
 * Add User Details content.
 *
 * @param defaultUsername - Default username to populate when creating a new user.
 * @param isAdmin - Is this user an administrator?
 * @param selectedUser - Selected user if this is an edit action.
 * @param hasPermissionsChanged - Flag to indicate if permissions have been changed, used to determine if password validation should be active.
 * @param languageConfig - Language options for the default language select input.
 * @param viewOnly - Flag to indicate if the form is in view only mode, which disables all inputs.
 * @param allRoles - List of all roles, used for validating the reporting level based on the selected role's requirements.
 * @param reportingLevelOptions - Options for the reporting level select input.
 * @param handleReportingLevelChange - Change handler for the reporting level select input.
 * @param isDesktopOnlyMode - Flag to indicate if the system is in desktop only mode, which forces the reporting level to none.
 * @param telSysType - The type of telephony system, used to determine how to display the CXI licence option.
 * @param showCxiUserField - Flag to indicate if the CXI User control should be displayed.
 * @param showCxiDataField - Flag to indicate if the CXI Data control should be displayed.
 *
 */
const UserDetails: React.FC<Props> = ({
    defaultUsername,
    isAdmin,
    selectedUser,
    hasPermissionsChanged,
    languageConfig,
    viewOnly,
    loadedCxsSubscription,
    loadedCxrSubscription,
    loadedDeviceMonitoring,
    loadedReportingLevel,
    allRoles,
    reportingLevelOptions,
    handleReportingLevelChange,
    isDesktopOnlyMode,
    telSysType,
    isCxAnalyticsApplicable,
    reportLevelOptionsLoading,
    isCxsSubscriptionApplicable,
    isCxsAgentScopeAllowed,
    businessIntegrationOptions,
    cxsTelsysMode,
    showCxiUserField,
    showCxiDataField,
    isCxsCmeCapabilityEnabled = true,
    isCxrCmeCapabilityEnabled = true,
    isTranscribeAvailable = false,
    isBroadWorksForTranscribe = false,
    isCxiCmeCapabilityEnabled = true,
    loadedCxiLicence
}: Props): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(UserDetailsLanguage.NAME)
    language.loadBundle(CxiLicenceTypes.NAME)
    language.loadBundle(Timezone.NAME)
    language.loadBundle(CXScoreLanguage.NAME)
    language.loadBundle(CXRecordLanguage.NAME)

    const userInfo: UserInfoContextType = useContext(UserInfoContext)

    // Contexts
    const {
        watch,
        errors,
        setValue,
        trigger,
        formState: { dirtyFields }
    }: UseFormMethods = useFormContext()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const cxIntegrationLicences: SelectOptions<SelectOptionValues> = useMemo(
        (): SelectOptions<SelectOptionValues> => [
            {
                value: CxIntegrationLicence.NONE,
                label: language.get(CxiLicenceTypes.CxiLicenceType_NONE)
            },
            {
                value: CxIntegrationLicence.SOLO,
                label: language.get(CxiLicenceTypes.CxiLicenceType_SOLO)
            },
            {
                value: CxIntegrationLicence.TEAMS,
                label: language.get(CxiLicenceTypes.CxiLicenceType_TEAMS)
            },
            {
                value: CxIntegrationLicence.CXI,
                label: language.get(CxiLicenceTypes.CxiLicenceType_CXI)
            }
        ],
        []
    )

    // Without the CME capability, a held CXI licence can only be kept or set to None (APD-13052).
    const visibleCxIntegrationLicences: SelectOptions<SelectOptionValues> = useMemo(
        (): SelectOptions<SelectOptionValues> =>
            isCxiCmeCapabilityEnabled
                ? cxIntegrationLicences
                : cxIntegrationLicences.filter(
                      (option: SelectOptions<SelectOptionValues>[number]): boolean =>
                          option.value === CxIntegrationLicence.NONE || option.value === loadedCxiLicence
                  ),
        [cxIntegrationLicences, isCxiCmeCapabilityEnabled, loadedCxiLicence]
    )

    const usernameValue: string = watch(USERNAME_COMPONENT_NAME)
    const emailValue: string = watch(EMAIL_COMPONENT_NAME)
    const isPasswordDefaulted: boolean = DEFAULT_PASSWORD_VALUE === watch(PASSWORD_COMPONENT_NAME)
    const cxiLicenceValue: CxIntegrationLicence = watch(CXI_LICENCE_NAME)
    const isCxiLicenceType: boolean = cxiLicenceValue === CxIntegrationLicence.CXI
    const isCxiDataEntitlementEnabled: boolean = !!watch(CXI_DATA_ENTITLEMENT_NAME)

    const [isUsernameDisabled, setIsUsernameDisabled]: ReactState<boolean> = useState<boolean>(!!selectedUser)
    const [isPasswordSet, setIsPasswordSet]: ReactState<boolean> = useState<boolean>(false)
    const isPasswordValidationActive: boolean =
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        !!dirtyFields?.details?.password ||
        (!isUsernameDisabled && usernameValue !== defaultUsername) ||
        (hasPermissionsChanged && !!isAdmin)

    const hasEmail: boolean = !IsStringEmpty(emailValue)
    const cxsSubscriptionValue: CXScoreLicences = watch(CXS_SUBSCRIPTION_NAME) as CXScoreLicences

    const showSendWelcomeEmail: boolean =
        hasEmail &&
        !getFieldError(EMAIL_COMPONENT_NAME, errors) &&
        ((selectedUser && !isPasswordValidationActive) || !IsStringEmpty(watch(PASSWORD_COMPONENT_NAME))) &&
        watch(PASSWORD_CHANGE_COMPONENT_NAME)

    const [isPasswordHidden, setIsPasswordHidden]: ReactState<boolean> = useState<boolean>(!!selectedUser)

    const reportingLevelValue: string = watch(REPORTING_LEVEL_NAME)

    const cxScoreLicenceSelectOptions = useMemo((): ReturnType<typeof cxsSelectOptionsForAddUser> => {
        const tierOptions: ReturnType<typeof cxsSelectOptionsForAddUser> = cxsSelectOptionsForAddUser(
            reportingLevelValue,
            (i18nKey: string): string => language.get(i18nKey),
            cxsTelsysMode
        )

        return isCxsAgentScopeAllowed ? tierOptions : withoutCxsAgentOption(tierOptions)
    }, [reportingLevelValue, cxsTelsysMode, isCxsAgentScopeAllowed])
    const isEssentialsUser: boolean = useMemo(
        (): boolean => reportingLevelValue === ReportingAccessLevels.ESSENTIALS,
        [reportingLevelValue]
    )
    const isProfessionalOrAgentUser: boolean = useMemo(
        (): boolean =>
            reportingLevelValue === ReportingAccessLevels.PROFESSIONAL ||
            reportingLevelValue === ReportingAccessLevels.CALL_CENTRE_AGENT,
        [reportingLevelValue]
    )

    const hasLoadedCxsLicence: boolean =
        isVarNotEmpty(loadedCxsSubscription) && loadedCxsSubscription !== CXScoreLicences.NONE

    const showCxsSubscriptionField: boolean = useMemo(
        (): boolean => isCxsSubscriptionApplicable && (isCxsCmeCapabilityEnabled || hasLoadedCxsLicence),
        [isCxsSubscriptionApplicable, isCxsCmeCapabilityEnabled, hasLoadedCxsLicence]
    )

    const hasReportingLevelChangedFromLoaded: boolean = useMemo(
        (): boolean => loadedReportingLevel !== undefined && reportingLevelValue !== loadedReportingLevel,
        [loadedReportingLevel, reportingLevelValue]
    )

    const shouldPreserveLoadedSubscription: boolean = useMemo(
        (): boolean =>
            shouldPreserveLoadedCxsSubscription(
                loadedCxsSubscription,
                cxsSubscriptionValue,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                !!dirtyFields?.reporting?.reportinglevel || hasReportingLevelChangedFromLoaded,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                !!dirtyFields?.details?.cxsSubscription
            ),
        [cxsSubscriptionValue, dirtyFields, hasReportingLevelChangedFromLoaded, loadedCxsSubscription]
    )

    const visibleCxScoreLicenceSelectOptions = useMemo((): ReturnType<typeof cxsSelectOptionsForAddUser> => {
        if (!isCxsCmeCapabilityEnabled) {
            return keepOnlyLoadedOrNone(
                ensureCurrentCxsOptionVisible(
                    cxScoreLicenceSelectOptions,
                    shouldPreserveLoadedSubscription ? loadedCxsSubscription : undefined,
                    (i18nKey: string): string => language.get(i18nKey)
                ),
                CXScoreLicences.NONE,
                loadedCxsSubscription
            )
        }

        return ensureCurrentCxsOptionVisible(
            cxScoreLicenceSelectOptions,
            shouldPreserveLoadedSubscription ? loadedCxsSubscription : undefined,
            (i18nKey: string): string => language.get(i18nKey)
        )
    }, [
        cxScoreLicenceSelectOptions,
        isCxsCmeCapabilityEnabled,
        loadedCxsSubscription,
        shouldPreserveLoadedSubscription
    ])

    const cxrSubscriptionValue: CXRecordLicences = watch(CXR_SUBSCRIPTION_NAME) as CXRecordLicences

    const cxrLicenceSelectOptions: CxRecordLicenceSelectOption[] = useMemo(
        (): CxRecordLicenceSelectOption[] =>
            cxrSelectOptionsForAddUser(reportingLevelValue, (i18nKey: string): string => language.get(i18nKey)),
        [reportingLevelValue]
    )

    /** Whether a restricted CXR Manager selection was replaced, so the reason can be shown. */
    const [wasCxrManagerReplaced, setWasCxrManagerReplaced] = useState<boolean>(false)

    const shouldPreserveLoadedCxrSub: boolean = useMemo(
        (): boolean =>
            shouldPreserveLoadedCxrSubscription(
                loadedCxrSubscription,
                cxrSubscriptionValue,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                !!dirtyFields?.reporting?.reportinglevel || hasReportingLevelChangedFromLoaded,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                !!dirtyFields?.details?.cxrSubscription
            ),
        [cxrSubscriptionValue, dirtyFields, hasReportingLevelChangedFromLoaded, loadedCxrSubscription]
    )

    const visibleCxrLicenceSelectOptions: CxRecordLicenceSelectOption[] = useMemo((): CxRecordLicenceSelectOption[] => {
        if (!isCxrCmeCapabilityEnabled) {
            return keepOnlyLoadedOrNone(
                ensureCurrentCxrOptionVisible(
                    cxrLicenceSelectOptions,
                    shouldPreserveLoadedCxrSub ? loadedCxrSubscription : undefined,
                    (i18nKey: string): string => language.get(i18nKey)
                ),
                CXRecordLicences.NONE,
                loadedCxrSubscription
            )
        }

        return ensureCurrentCxrOptionVisible(
            cxrLicenceSelectOptions,
            shouldPreserveLoadedCxrSub ? loadedCxrSubscription : undefined,
            (i18nKey: string): string => language.get(i18nKey)
        )
    }, [cxrLicenceSelectOptions, isCxrCmeCapabilityEnabled, loadedCxrSubscription, shouldPreserveLoadedCxrSub])

    // CXR requires the CX-Record business integration option (MS Teams Business only) — the same
    // product rule as the Licence Management page. CXR is available at every reporting level (APD-12899);
    // the per-level option filtering (e.g. CXR Manager only for None/Manager) lives in cxrSelectOptionsForAddUser.
    const hasLoadedCxrLicence: boolean =
        isVarNotEmpty(loadedCxrSubscription) && loadedCxrSubscription !== CXRecordLicences.NONE

    const showCxrSubscriptionField: boolean = useMemo(
        (): boolean =>
            isCxrProductApplicable(businessIntegrationOptions) && (isCxrCmeCapabilityEnabled || hasLoadedCxrLicence),
        [businessIntegrationOptions, isCxrCmeCapabilityEnabled, hasLoadedCxrLicence]
    )

    const isTranscribeEnabled: boolean = !!watch(CXT_TRANSCRIBE_ENABLED_NAME)
    // CX Score and CX Record switched on for the user's device qualify them for Transcribe without a licence
    // (APD-14067 AC4). These are the flags the user loaded with, whatever device the admin picks now: the backend
    // validation reads them from the device the user has before the save. The licence dropdowns never change them
    // either: the modal cannot switch device monitoring off, as the save mapper only ever forces a flag on for the tier
    // that needs it and otherwise sends no change. So removing a licence from a user whose device still scores or
    // records leaves them qualified, which is what the backend does.

    // Transcribe shows whenever the flag is on and the CME has CX Transcribe for the TelSys type. It can only be ticked
    // by a user who qualifies through CX Score or CX Record (APD-14047 matrix); without one it is greyed out and off.
    const showTranscribeField: boolean = isTranscribeAvailable
    const isQualifiedForTranscribe: boolean = isTranscribeQualifiedForDevice(
        cxsSubscriptionValue,
        cxrSubscriptionValue,
        loadedDeviceMonitoring,
        isBroadWorksForTranscribe
    )

    // CX Analytics is the reporting-level dropdown. It shows when reporting is available on the telsys
    // (not CXI-only mode) and CX Analytics applies to it (business-option gated on MS Teams).
    const showReportingLevel: boolean = !isDesktopOnlyMode && isCxAnalyticsApplicable

    // The "User Subscriptions" sub-header groups the licence/subscription fields (CX Analytics,
    // CX Score, CX Integration, CX Record). Only render it when at least one of those fields shows.
    const showSubscriptionsSection: boolean =
        showReportingLevel || showCxiUserField || showCxsSubscriptionField || showCxrSubscriptionField

    // When the CX Analytics dropdown is hidden only because the telsys no longer carries the
    // CX-Analytics option, keep an existing user's loaded (grandfathered) reporting level rather than
    // resetting it — dropping it would conflict with a role that requires reporting. Fresh users have
    // no loaded level and CXI-only telsys (isDesktopOnlyMode) does no reporting at all, so both still
    // normalise to None.
    const shouldPreserveHiddenReportingLevel: boolean =
        !isDesktopOnlyMode && loadedReportingLevel !== undefined && !hasReportingLevelChangedFromLoaded

    useEffect((): void => {
        const userReportingLevelShouldBeRemoved: boolean =
            !showReportingLevel &&
            reportingLevelValue !== ReportingAccessLevels.NONE &&
            !shouldPreserveHiddenReportingLevel

        if (userReportingLevelShouldBeRemoved) {
            const prevReportingLevel: string = reportingLevelValue
            const newReportingLevel: string = ReportingAccessLevels.NONE

            setValue(REPORTING_LEVEL_NAME, newReportingLevel)
            handleReportingLevelChange(newReportingLevel, prevReportingLevel)
        }
    }, [reportingLevelValue, showReportingLevel, shouldPreserveHiddenReportingLevel])

    const previousTranscribeLicences: MutableRefObject<Definable<TranscribeLicences>> = useRef<TranscribeLicences>()

    // Transcribe turns off when the user loses their last qualifier through a licence field on screen: they qualified a
    // moment ago, no longer do, and the CX Score or CX Record value that changed is one they can see. Nothing else
    // changes it: not loading a user (so the form isn't dirty on open), not a feature flag that is off or a CME without
    // CX Transcribe (the toggle is just hidden), and not a licence field that another rule hid and reset, so none of
    // those wipe a saved setting on the next save.
    useEffect((): void => {
        const isQualified: boolean = isQualifiedForTranscribe
        const previous: Definable<TranscribeLicences> = previousTranscribeLicences.current

        previousTranscribeLicences.current = { cxs: cxsSubscriptionValue, cxr: cxrSubscriptionValue, isQualified }

        if (!previous?.isQualified || isQualified || !watch(CXT_TRANSCRIBE_ENABLED_NAME)) return

        const isVisibleChange: boolean =
            (showCxsSubscriptionField && previous.cxs !== cxsSubscriptionValue) ||
            (showCxrSubscriptionField && previous.cxr !== cxrSubscriptionValue)

        if (isVisibleChange) {
            setValue(CXT_TRANSCRIBE_ENABLED_NAME, false, { shouldDirty: true })
        }
    }, [
        cxsSubscriptionValue,
        cxrSubscriptionValue,
        isQualifiedForTranscribe,
        showCxsSubscriptionField,
        showCxrSubscriptionField
    ])

    useEffect((): void => {
        if (!showSendWelcomeEmail && watch(SEND_WELCOME_COMPONENT_NAME)) {
            setValue(SEND_WELCOME_COMPONENT_NAME, false)
        }
    }, [showSendWelcomeEmail])

    useLayoutEffect((): void => {
        if (!showCxsSubscriptionField) return

        if (shouldPreserveLoadedSubscription) {
            return
        }

        const tierReplacement: CXScoreLicences = cxsReplacementForUserType(
            cxsSubscriptionValue,
            reportingLevelValue,
            cxsTelsysMode
        )

        const validReplacement: CXScoreLicences = isCxsAgentScopeAllowed
            ? tierReplacement
            : cxsAgentScopeReplacement(tierReplacement)

        if (validReplacement === cxsSubscriptionValue) return

        // Without the CME capability the licence can only be kept or removed, never swapped for another tier.
        const replacement: CXScoreLicences = isCxsCmeCapabilityEnabled ? validReplacement : CXScoreLicences.NONE

        setValue(CXS_SUBSCRIPTION_NAME, replacement)
        trigger(ENDPOINT_DEVICE_NAME)
        trigger(ENDPOINT_AGENT_NAME)
    }, [
        cxsSubscriptionValue,
        shouldPreserveLoadedSubscription,
        reportingLevelValue,
        cxsTelsysMode,
        isCxsAgentScopeAllowed,
        isCxsCmeCapabilityEnabled,
        setValue,
        showCxsSubscriptionField,
        trigger
    ])

    useLayoutEffect((): void => {
        if (telSysType !== TelSysTypes.MS_TEAMS) return

        // The level permits CXR Manager, so nothing is replaced and any earlier notice no longer applies.
        if (isCxrManagerAllowedLevel(reportingLevelValue)) {
            setWasCxrManagerReplaced(false)

            return
        }

        if (shouldPreserveLoadedCxrSub) return

        const validReplacement: CXRecordLicences = cxrRestrictedReplacement(cxrSubscriptionValue, reportingLevelValue)

        if (validReplacement === cxrSubscriptionValue) return

        // Without the CME capability the licence can only be kept or removed, never swapped for another one.
        const replacement: CXRecordLicences = isCxrCmeCapabilityEnabled ? validReplacement : CXRecordLicences.NONE

        setValue(CXR_SUBSCRIPTION_NAME, replacement)
        // Record that the subscription was replaced so the form can say why, rather than the value
        // changing under the administrator with no explanation (APD-13617).
        setWasCxrManagerReplaced(true)
    }, [
        cxrSubscriptionValue,
        shouldPreserveLoadedCxrSub,
        reportingLevelValue,
        telSysType,
        setValue,
        isCxrCmeCapabilityEnabled
    ])

    useLayoutEffect((): void => {
        if (isCxsSubscriptionApplicable && isCxsCmeCapabilityEnabled) return

        const current: string = watch(CXS_SUBSCRIPTION_NAME)

        if (current === CXScoreLicences.NONE) return

        if (shouldPreserveLoadedSubscription) {
            return
        }

        // The CME no longer allows CX Score: a licence the user already holds is kept, as the backend accepts it unchanged.
        if (isCxsSubscriptionApplicable && current === loadedCxsSubscription) return

        setValue(CXS_SUBSCRIPTION_NAME, CXScoreLicences.NONE)
        trigger(ENDPOINT_DEVICE_NAME)
        trigger(ENDPOINT_AGENT_NAME)
    }, [
        isCxsSubscriptionApplicable,
        isCxsCmeCapabilityEnabled,
        loadedCxsSubscription,
        setValue,
        shouldPreserveLoadedSubscription,
        trigger,
        watch
    ])

    useLayoutEffect((): void => {
        if (isCxrCmeCapabilityEnabled) return

        const current: string = watch(CXR_SUBSCRIPTION_NAME)

        // A licence the user already holds is kept, as the backend accepts it unchanged.
        if (current === CXRecordLicences.NONE || current === loadedCxrSubscription) return

        setValue(CXR_SUBSCRIPTION_NAME, CXRecordLicences.NONE)
    }, [isCxrCmeCapabilityEnabled, loadedCxrSubscription, setValue, watch])

    return (
        <div className='user-details-tab tab-area'>
            {showSubscriptionsSection && (
                <Heading level={HeadingLevel.TWO} className='user-details-section-heading'>
                    {language.get(UserDetailsLanguage.UserDetailsLanguage_UserSubscriptions)}
                </Heading>
            )}
            {showReportingLevel && (
                <FormRow className='reporting-level'>
                    <ValidationErrors name={REPORTING_LEVEL_NAME}>
                        <GroupedSelect
                            variant='outlined'
                            loading={reportLevelOptionsLoading}
                            name={REPORTING_LEVEL_NAME}
                            label={language.get(ReportingLanguage.ReportingLevel)}
                            options={
                                reportingLevelOptions ?? [
                                    {
                                        groupTitle: EMPTY_STRING,
                                        options: [{ label: EMPTY_STRING, value: reportingLevelValue }]
                                    }
                                ]
                            }
                            defaultValue={SERVER_NONE_VALUE}
                            readOnly
                            handleChange={handleReportingLevelChange}
                            rules={{
                                validate: (reportingLevel: string): ValidateResult => {
                                    const role: Definable<RoleTemplate> =
                                        allRoles[watch<string, number>(ROLE_COMPONENT_NAME)]

                                    if (role && !IsVarEmpty(role.requiresReporting)) {
                                        const isReportingLevel: boolean =
                                            !IsStringEmpty(reportingLevel) && reportingLevel !== SERVER_NONE_VALUE
                                        const errorMessage: string = !isReportingLevel
                                            ? language.get(RoleLanguage.RoleRequiresReportingLevel)
                                            : language.get(RoleLanguage.RoleRequiresNoReportingLevel)

                                        return role.requiresReporting === isReportingLevel ? undefined : errorMessage
                                    }

                                    return undefined
                                }
                            }}
                            disabled={viewOnly}
                        />
                    </ValidationErrors>
                </FormRow>
            )}
            {showCxsSubscriptionField ? (
                <FormRow>
                    <OutlinedSelect
                        name={CXS_SUBSCRIPTION_NAME}
                        label={language.get(UserDetailsLanguage.UserDetailsLanguage_CXS_Subscription)}
                        disabled={viewOnly}
                        options={visibleCxScoreLicenceSelectOptions}
                        handleChange={(): void => {
                            trigger(ENDPOINT_DEVICE_NAME)
                            trigger(ENDPOINT_AGENT_NAME)
                        }}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            {showCxiUserField ? (
                <FormRow>
                    {telSysType === TelSysTypes.WEBEX_CALLING || telSysType === TelSysTypes.MS_TEAMS ? (
                        <RouterCheckbox
                            isChecked={isCxiLicenceType}
                            labelLeft={language.get(UserDetailsLanguage.UserDetailsLanguage_CXIUser)}
                            disabled={viewOnly}
                            onChange={(): void =>
                                setValue(
                                    CXI_LICENCE_NAME,
                                    isCxiLicenceType ? CxIntegrationLicence.NONE : CxIntegrationLicence.CXI
                                )
                            }
                        />
                    ) : (
                        <OutlinedSelect
                            name={CXI_LICENCE_NAME}
                            disabled={viewOnly}
                            label={language.get(UserDetailsLanguage.UserDetailsLanguage_CXIUser)}
                            options={visibleCxIntegrationLicences}
                        />
                    )}
                </FormRow>
            ) : null}
            {showCxiDataField ? (
                <FormRow>
                    <RouterCheckbox
                        isChecked={isCxiDataEntitlementEnabled}
                        labelLeft={language.get(UserDetailsLanguage.UserDetailsLanguage_CXIData)}
                        disabled={viewOnly}
                        onChange={(): void => setValue(CXI_DATA_ENTITLEMENT_NAME, !isCxiDataEntitlementEnabled)}
                    />
                </FormRow>
            ) : null}
            {showCxrSubscriptionField && (
                <FormRow>
                    <OutlinedSelect
                        name={CXR_SUBSCRIPTION_NAME}
                        label={language.get(UserDetailsLanguage.UserDetailsLanguage_CXR_Subscription)}
                        disabled={viewOnly}
                        options={visibleCxrLicenceSelectOptions}
                    />
                </FormRow>
            )}
            {showTranscribeField && (
                <FormRow>
                    {isQualifiedForTranscribe ? (
                        <RouterCheckbox
                            isChecked={isTranscribeEnabled}
                            labelLeft={language.get(UserDetailsLanguage.UserDetailsLanguage_CXT_TranscribeCalls)}
                            disabled={viewOnly}
                            onChange={(): void =>
                                setValue(CXT_TRANSCRIBE_ENABLED_NAME, !isTranscribeEnabled, { shouldDirty: true })
                            }
                        />
                    ) : (
                        <InputHelp
                            text={language.get(
                                isBroadWorksForTranscribe
                                    ? UserDetailsLanguage.UserDetailsLanguage_CXT_NeedsCxsLicence
                                    : UserDetailsLanguage.UserDetailsLanguage_CXT_NeedsCxsOrCxrLicence
                            )}
                        >
                            <RouterCheckbox
                                isChecked={false}
                                labelLeft={language.get(UserDetailsLanguage.UserDetailsLanguage_CXT_TranscribeCalls)}
                                disabled
                            />
                        </InputHelp>
                    )}
                </FormRow>
            )}
            {showCxrSubscriptionField && wasCxrManagerReplaced && (
                <FormRow>
                    <span className='user-details-cxr-notice'>
                        {language.get(UserDetailsLanguage.UserDetailsLanguage_CXR_ManagerNotAllowed)}
                    </span>
                </FormRow>
            )}
            <Heading level={HeadingLevel.TWO} className='user-details-section-heading'>
                {language.get(UserDetailsLanguage.UserDetailsLanguage_UserConfiguration)}
            </Heading>
            <FormRow className='full-name'>
                <ValidationErrors name={FULLNAME_COMPONENT_NAME}>
                    <OutlinedTextInput
                        placeholder={language.get(UserDetailsLanguage.FullNamePlaceholder)}
                        labelLeft={language.get(UserDetailsLanguage.FullName)}
                        inputName={FULLNAME_COMPONENT_NAME}
                        rules={{
                            maxLength: {
                                value: 100,
                                message: language.get(UserDetailsLanguage.NameValidation)
                            }
                        }}
                        disabled={viewOnly}
                    />
                </ValidationErrors>
            </FormRow>
            <FormRow className='username'>
                <ValidationErrors name={USERNAME_COMPONENT_NAME}>
                    <InputHelp
                        text={
                            isEssentialsUser || isProfessionalOrAgentUser
                                ? language.get(UserDetailsLanguage.UsernameIDOnTelSysHint)
                                : language.get(UserDetailsLanguage.UsernameHint)
                        }
                    >
                        <OutlinedTextInput
                            autoComplete='username'
                            labelLeft={language.get(UserDetailsLanguage.UserDetailsLanguage_Username)}
                            inputName={USERNAME_COMPONENT_NAME}
                            rules={{
                                required: {
                                    value: true,
                                    message: isEssentialsUser
                                        ? language.get(UserDetailsLanguage.UsernameMustBeSetFromDeviceValidation)
                                        : language.get(UserDetailsLanguage.UsernameRequiredValidation)
                                },
                                maxLength: {
                                    value: 255,
                                    message: language.get(UserDetailsLanguage.UsernameMaxLengthValidation)
                                }
                            }}
                            onBlur={(value?: string): void => {
                                if (!hasEmail && isStringNotEmpty(value) && RulesUtils.isEmailValid(value)) {
                                    setValue(EMAIL_COMPONENT_NAME, value)
                                    trigger(EMAIL_COMPONENT_NAME)
                                }
                            }}
                            onChange={(): void => {
                                if (!isUsernameDisabled && selectedUser) {
                                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                                    if (dirtyFields.details.password || isPasswordSet) return

                                    getRandomPassword().then((password: Nullable<string>): void => {
                                        if (isMounted.current) {
                                            setValue(PASSWORD_COMPONENT_NAME, password ?? EMPTY_STRING)
                                            setIsPasswordSet(true)
                                            setIsPasswordHidden(false)
                                            trigger(PASSWORD_COMPONENT_NAME)
                                        }
                                    })
                                }
                            }}
                            adornmentEnd={
                                selectedUser && !viewOnly && !(isEssentialsUser || isProfessionalOrAgentUser) ? (
                                    <InputAdornmentButton
                                        iconName={isUsernameDisabled ? IconName.PENCIL : IconName.X}
                                        onClick={(): void => {
                                            if (isUsernameDisabled) {
                                                setIsUsernameDisabled(false)
                                            } else {
                                                setValue(USERNAME_COMPONENT_NAME, defaultUsername)
                                                setIsUsernameDisabled(true)
                                                trigger(PASSWORD_COMPONENT_NAME)
                                            }
                                        }}
                                        popperText={language.get(UserDetailsLanguage.UsernameEditHint)}
                                    />
                                ) : undefined
                            }
                            disabled={isUsernameDisabled || viewOnly || isEssentialsUser || isProfessionalOrAgentUser}
                        />
                    </InputHelp>
                </ValidationErrors>
            </FormRow>
            <FormRow className='email'>
                <ValidationErrors name={EMAIL_COMPONENT_NAME}>
                    <InputHelp text={language.get(UserDetailsLanguage.EmailHint)}>
                        <OutlinedTextInput
                            autoComplete='username'
                            labelLeft={language.get(UserDetailsLanguage.Email)}
                            placeholder={language.get(UserDetailsLanguage.EmailPlaceholder)}
                            inputName={EMAIL_COMPONENT_NAME}
                            rules={{
                                required: language.get(UserDetailsLanguage.EmailRequiredValidation),
                                maxLength: {
                                    value: 255,
                                    message: language.get(UserDetailsLanguage.EmailMaxLengthValidation)
                                },
                                validate: {
                                    isEmailAddress: (email: string): ValidateResult => {
                                        if (!IsStringEmpty(email) && !email?.match(EMAIL_REGEX)) {
                                            return language.get(UserDetailsLanguage.EmailPatternValidation)
                                        }

                                        return undefined
                                    }
                                }
                            }}
                            disabled={viewOnly}
                        />
                    </InputHelp>
                </ValidationErrors>
            </FormRow>
            <FormRow className='password'>
                <ValidationErrors name={PASSWORD_COMPONENT_NAME}>
                    <OutlinedPasswordTextInput
                        defaultToRandom={IsStringEmpty(selectedUser)}
                        defaultValue={DEFAULT_PASSWORD_VALUE}
                        passwordHiddenState={[isPasswordHidden, setIsPasswordHidden]}
                        labelLeft={language.get(UserDetailsLanguage.UserDetailsLanguage_Password)}
                        inputName={PASSWORD_COMPONENT_NAME}
                        placeholder={language.get(UserDetailsLanguage.UserDetailsLanguage_PasswordPlaceholder)}
                        isAdmin={isAdmin}
                        isRandomiserEnabled={!viewOnly}
                        isRandomiserAdornment
                        isPasswordCheckerEnabled={
                            IsStringEmpty(selectedUser) || (!IsStringEmpty(selectedUser) && isPasswordValidationActive)
                        }
                        disabled={viewOnly}
                        autoComplete={AutocompleteValues.NEW_PASSWORD}
                    />
                </ValidationErrors>
            </FormRow>
            {languageConfig && userInfo && (
                <FormRow className='default-language'>
                    <InputHelp text={language.get(UserDetailsLanguage.DefaultlanguageHint)}>
                        <OutlinedSelect
                            name={DEFAULT_LANG_COMPONENT_NAME}
                            label={language.get(UserDetailsLanguage.Defaultlanguage)}
                            options={languageConfig}
                            defaultValue={userInfo?.SessionInfo?.Language ?? LANGUAGE_DEFAULT}
                            disabled={viewOnly}
                        />
                    </InputHelp>
                </FormRow>
            )}
            <FormRow className='timezone-change-usermodal'>
                <OutlinedSelect
                    name={TIMEZONE_COMPONENT_NAME}
                    label={language.get(UserDetailsLanguage.Timezone_Title)}
                    options={timezonesToSelectOptions(language, Timezone as unknown as TimezoneKeys)}
                    defaultValue={DEFAULT_TIMEZONE}
                    disabled={viewOnly}
                />
            </FormRow>
            <FormRow className='password-change'>
                <RouterCheckbox
                    labelLeft={language.get(UserDetailsLanguage.Passwordchange)}
                    name={PASSWORD_CHANGE_COMPONENT_NAME}
                    defaultChecked={true}
                    disabled={viewOnly}
                />
            </FormRow>
            <FormRow className='welcome-email'>
                <InputHelp text={language.get(UserDetailsLanguage.WelcomeemailHelp)}>
                    <RouterCheckbox
                        disabled={!showSendWelcomeEmail || viewOnly || isPasswordDefaulted}
                        labelLeft={language.get(UserDetailsLanguage.Welcomeemail)}
                        name={SEND_WELCOME_COMPONENT_NAME}
                    />
                </InputHelp>
            </FormRow>
            <FormRow className='mfa'>
                <InputHelp text={language.get(UserDetailsLanguage.UserDetailsLanguage_MFAHelp)}>
                    <RouterCheckbox
                        labelLeft={language.get(UserDetailsLanguage.UserDetailsLanguage_MFA_Label)}
                        name={ENABLE_MFA_COMPONENT_NAME}
                        disabled={viewOnly}
                    />
                </InputHelp>
            </FormRow>
        </div>
    )
}

export default UserDetails
